
import SpmTable from '@/components/table/SpmTable.vue';
import {
  SpmTableAction,
  SpmTableColumns,
  UserTypeEnum,
  StatsType,
  SpmTableState,
} from '@/types';
import { useI18n } from 'vue-i18n';
import { UserState } from '@/composables/User';
import { defineComponent, onBeforeMount, ref } from 'vue';
import {
  ShopsUsers,
  UsersPagesSubscriptionsDeleteInput,
} from '@/types/generated-types/graphql';
import { DeleteUsersPermissions, getListOfShopSubUsers, shopUserList } from '@/composables/user/ShopsUsers';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { showToastError, showToastSuccess } from '@/helpers';
import SecureSpmButton from '@/components/spm-primevue/SecureSpmButton.vue';
import {
  DeleteUsersPagesSubscriptions,
  getUserStatsPages,
  getUserStatsPagesV2,
} from '@/composables/Statistics/usersPagesSubscriptions';
// eslint-disable-next-line import/no-cycle
import router from '@/router';

export default defineComponent({
  name: 'SubUserList',

  components: {
    SpmTable,
    Button,
    Dialog,
    SecureSpmButton,
  },

  setup() {
    const { t } = useI18n();
    const idShop = UserState?.activeShop?.id ?? 0;
    const dialogVisible = ref<boolean>(false);
    const idUserToDelete = ref(0);
    const loadingInitiateAction = ref(false);
    const showDialog = async (idUser: number) => {
      idUserToDelete.value = idUser;
      dialogVisible.value = true;
    };

    const tableData = ref<SpmTableState>({
      items: [],
      total: 1,
      error: '',
      selectedItems: [],
    });

    const actions: SpmTableAction[] = [
      {
        icon: 'fas fa-pencil-alt',
        label: t('user.subUser.modify.menuItem'),
        callback: (data: any, event: {originalEvent: Event; item: any; navigate: undefined}) => {
          if (event) {
            router.push({
              name: 'user.manage-sub-users',
              params: { userId: data.id_shops_user },
            });
          }
        },

        show: () => true,
      },
      {
        icon: 'fa-regular fa-trash-can',
        label: t('myLists.manage.bulkActions.delete.menuItem'),
        callback: (data: any, event: {originalEvent: Event; item: any; navigate: undefined}) => {
          if (event) {
            dialogVisible.value = true;
            showDialog(data.id_shops_user);
          }
        },

        show: () => true,
      },
    ];

    const columns: SpmTableColumns[] = [
      {
        field: 'id_shops_user',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'first_name',
        header: t('myLists.manage.customers.headers.firstname'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'last_name',
        header: t('myLists.manage.customers.headers.lastname'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'email',
        header: t('myLists.manage.customers.headers.email'),
        sortable: false,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'lang',
        header: t('myLists.manage.customers.headers.lang'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'language',
      },
      {
        field: 'expiry_date',
        header: t('user.subUser.permissions.expiry.column_header'),
        sortable: true,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: '',
        header: t('myLists.manage.actions.placeholder'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'action',
        actions,
      },
    ];

    const reloadDataTable = async () => {
      const dataList = await getListOfShopSubUsers(UserState.user.idPsCustomer);
      if (dataList && dataList.success) {
        tableData.value.items = dataList.items ?? [];
        tableData.value.total = dataList.total ?? 0;
      } else {
        tableData.value.items = [];
        tableData.value.total = 0;
      }
    };

    const deleteSpecificUser = async () => {
      loadingInitiateAction.value = true;
      const result = await DeleteUsersPermissions([{
        id_shops_user: idUserToDelete.value,
        id_ps_customer: UserState.user.idPsCustomer,
      }]);

      if (result.err || !result.status) {
        const usersPagesSubscriptionsDelete: UsersPagesSubscriptionsDeleteInput[] = [];

        const dashboards = await getUserStatsPagesV2(idUserToDelete.value, UserTypeEnum.USER, StatsType.DASHBOARD, UserState.activeShop?.idUser);

        if (dashboards && dashboards.success && dashboards.items.length) {
          dashboards.items.forEach((otherDashboard: any) => {
            usersPagesSubscriptionsDelete.push({
              id_users_pages_configuration: otherDashboard.id_users_pages_configuration,
              id_user: idUserToDelete.value,
              user_type: UserTypeEnum.USER,
            });
          });
        }
        const stats = await getUserStatsPagesV2(idUserToDelete.value, UserTypeEnum.USER, StatsType.STATS, UserState.activeShop?.idUser);

        if (stats && stats.success && stats.items.length) {
          stats.items.forEach((otherStatsPage: any) => {
            usersPagesSubscriptionsDelete.push({
              id_users_pages_configuration: otherStatsPage.id_users_pages_configuration,
              id_user: idUserToDelete.value,
              user_type: UserTypeEnum.USER,
            });
          });
        }
        const emails = await getUserStatsPagesV2(idUserToDelete.value, UserTypeEnum.USER, StatsType.EMAIL, UserState.activeShop?.idUser);

        if (emails && emails.success && emails.items.length) {
          emails.items.forEach((otherEmailPage: any) => {
            usersPagesSubscriptionsDelete.push({
              id_users_pages_configuration: otherEmailPage.id_users_pages_configuration,
              id_user: idUserToDelete.value,
              user_type: UserTypeEnum.USER,
            });
          });
        }
        await DeleteUsersPagesSubscriptions(usersPagesSubscriptionsDelete);
        await showToastError(t('user.subUser.delete.deleteError'));
      } else {
        await showToastSuccess(t('user.subUser.delete.deleteSuccess'));
      }
      await reloadDataTable();

      dialogVisible.value = false;
      loadingInitiateAction.value = false;
    };

    const initiateAction = async () => {
      if (idUserToDelete.value !== 0) {
        await deleteSpecificUser();
        idUserToDelete.value = 0;
        return true;
      }
      return true;
    };

    const hideDialog = async () => {
      idUserToDelete.value = 0;
      dialogVisible.value = false;
    };

    const handleCreateUser = async () => {
      await router.push({
        name: 'user.manage-sub-users',
      });
    };

    const isExpiryDateValid = (expiryDate: string | null): boolean => {
      if (!expiryDate) return false;
      const date = new Date(expiryDate);
      const now = new Date();
      return date > now;
    };

    const formatExpiryDateOnly = (expiryDate: string): string => {
      const date = new Date(expiryDate);
      return date.toLocaleDateString();
    };

    const formatExpiryTimeOnly = (expiryDate: string): string => {
      const date = new Date(expiryDate);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    onBeforeMount(async () => {
      await reloadDataTable();
    });

    return {
      t,
      columns,
      tableData,
      dialogVisible,
      initiateAction,
      idUserToDelete,
      hideDialog,
      handleCreateUser,
      isExpiryDateValid,
      formatExpiryDateOnly,
      formatExpiryTimeOnly,
      loadingInitiateAction,
    };
  },
});
